import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { resolveIpfsUrl } from './constants/gameConfig';

// 1. Redundancy: If one node is down, we use the next.
const ATOMIC_ENDPOINTS = [
    "https://wax.api.atomicassets.io",       // Default
    "https://wax-atomic-api.eosphere.io",    // Backup 1
    "https://aa.neftyblocks.com",            // Backup 2
    "https://wax-aa.eosamsterdam.net"        // Backup 3
];

const DailyBoosts = ({ session, playerData }) => {
    const [userEmblems, setUserEmblems] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [activating, setActivating] = useState(null);
    const [feedback, setFeedback] = useState('');

    const syncPlayerVaultStateCallable = httpsCallable(functions, 'syncPlayerVaultState');

    // Configuration for all Boost Emblems
    const BOOST_CONFIG = {
        "872658": { id: "872658", name: "Forge Fury", type: "MP Boost", val: "+10%", key: "boost_atk", img: "QmPt9RQwUyRT16khUXK52BTQGpSJUaaXasg1PZZS7ksLbA" },
        "886383": { id: "886383", name: "Workshop Plating", type: "Speed Boost", val: "+10%", key: "boost_def", img: "QmRM5q7ABiS6ssm4pPSAhQbteDj9wCbaTwfUtM6jhnC1go" },
        "884183": { id: "884183", name: "Bastion Vitality", type: "Max HP", val: "+10%", key: "boost_hp", img: "QmRGaPhjjLPbHpwbNtavMqL3sPHivCnBn6z9s4dvX4QkvW" },
        "841646": { id: "841646", name: "Observatory Focus", type: "Points Gain", val: "+10%", key: "boost_pts", img: "QmYcAjeREb3oTpGoGhCth53qZ1uWhgKHVVzr1mPHJAH8vD" },
        "871245": { id: "871245", name: "Altar Fortune", type: "Luck (Drop Rate)", val: "+10%", key: "boost_luck", img: "QmSkbfQuXxJVhnEn62SpdZmCbzJFT4XbqonvnJLRuJU1Ww" },
        "872679": { id: "872679", name: "Scholar's Wisdom", type: "XP Gain", val: "+10%", key: "boost_xp", img: "QmZhQip3eXU5zxuvrUGtL1d2PGsyMLzEyzB1XJ8CR4iwE7" },
        "837486": { id: "837486", name: "Barracks Ration", type: "Max Stamina", val: "+10", key: "boost_stam_max", img: "Qmdmz2rTNSaS117Qmh7bGCBn9uYeiQ4fgMreWoegTgEZjW" }

    };

    // 2. Smart Fetcher: loops through endpoints until one works
    const fetchWithFallback = async (path) => {
        for (const endpoint of ATOMIC_ENDPOINTS) {
            try {
                const url = `${endpoint}${path}`;
                const response = await fetch(url);
                if (response.ok) {
                    return await response.json();
                }
            } catch (err) {
                console.warn(`Endpoint ${endpoint} failed, switching...`);
            }
        }
        throw new Error("All WAX API endpoints are currently busy.");
    };

    useEffect(() => {
        if (!session?.actor) return;

        let isMounted = true;

        const fetchEmblems = async () => {
            setIsLoading(true);
            const foundEmblems = {};
            // We scan BOTH collections
            const collectionsToScan = ['whatifreward', 'idaretodream'];

            try {
                // Use the smart fetcher instead of a hardcoded list
                for (const col of collectionsToScan) {
                    try {
                        const data = await fetchWithFallback(`/atomicassets/v1/assets?owner=${session.actor}&collection_name=${col}&limit=100`);

                        if (data && data.success) {
                            data.data.forEach(asset => {
                                const templateId = asset.template.template_id;
                                if (BOOST_CONFIG[templateId]) {
                                    if (!foundEmblems[templateId]) {
                                        foundEmblems[templateId] = [];
                                    }
                                    foundEmblems[templateId].push(asset.asset_id);
                                }
                            });
                        }
                    } catch (e) {
                        console.warn(`Skipping collection ${col} due to network error.`);
                    }
                }

                if (isMounted) setUserEmblems(foundEmblems);
            } catch (error) {
                if (isMounted) console.error("Error fetching emblems:", error);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchEmblems();
        return () => { isMounted = false; };
    }, [session]);

    const handleActivate = async (templateId) => {
        if (!session || !userEmblems[templateId] || userEmblems[templateId].length === 0) {
            setFeedback("No emblem found to activate.");
            return;
        }

        const assetId = userEmblems[templateId][0];
        const config = BOOST_CONFIG[templateId];

        if (!window.confirm(`Activate ${config.name}? This will BURN the NFT to grant a ${config.val} ${config.type} for 24 hours.`)) return;

        setActivating(templateId);
        setFeedback(`Activating ${config.name}... Please sign the transaction.`);

        try {
            const action = {
                account: 'atomicassets',
                name: 'transfer',
                authorization: [session.permissionLevel],
                data: {
                    from: session.actor.toString(),
                    to: 'whatifdragon',
                    asset_ids: [assetId],
                    memo: 'burn_boost'
                }
            };

            const txResult = await session.transact({ actions: [action] });

            setFeedback("Transaction sent! Verifying activation...");

            // Wait for chain propagation (Critical for the backend to see it)
            await new Promise(r => setTimeout(r, 8000));

            await syncPlayerVaultStateCallable();

            setFeedback(`✅ ${config.name} Activated!`);

            // Optimistic Update: Remove the used asset
            setUserEmblems(prev => ({
                ...prev,
                [templateId]: prev[templateId].filter(id => id !== assetId)
            }));

        } catch (error) {
            console.error("Activation Error:", error);
            setFeedback(`Error: ${error.message}`);
        } finally {
            setActivating(null);
        }
    };

    const now = Date.now();

    if (!session) return null;

    return (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', maxWidth: '800px', margin: '20px auto', border: '1px solid #444' }}>
            <h2 style={{ borderBottom: '1px solid #ff4b2b', paddingBottom: '10px', color: '#fff' }}>Daily Emblem Boosts</h2>
            <p style={{ color: '#ccc' }}>Burn an Emblem to gain a powerful boost for 24 hours. Boosts stack with Gear and Gang bonuses!</p>

            {feedback && <p style={{ padding: '10px', background: '#333', borderRadius: '5px', color: feedback.startsWith('Error') ? 'red' : 'lightgreen' }}>{feedback}</p>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginTop: '20px' }}>
                {Object.values(BOOST_CONFIG).map(boost => {
                    const isActive = playerData?.activeBoosts?.[boost.key] > now;
                    const expiryDate = isActive ? new Date(playerData.activeBoosts[boost.key]).toLocaleString() : null;
                    const count = userEmblems[boost.id]?.length || 0;

                    return (
                        <div key={boost.id} style={{
                            border: isActive ? '2px solid #00ff00' : '1px solid #555',
                            background: isActive ? 'rgba(0, 50, 0, 0.5)' : 'rgba(255,255,255,0.05)',
                            borderRadius: '8px',
                            padding: '15px',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {isActive && <div style={{ position: 'absolute', top: 0, right: 0, background: '#00ff00', color: 'black', fontSize: '10px', padding: '2px 6px', fontWeight: 'bold' }}>ACTIVE</div>}

                            <img src={resolveIpfsUrl(boost.img)} alt={boost.name} style={{ width: '60px', height: '60px', objectFit: 'contain', marginBottom: '10px' }} />

                            <div style={{ fontWeight: 'bold', color: '#ff9800' }}>{boost.name}</div>
                            <div style={{ fontSize: '12px', color: '#ccc' }}>{boost.type}</div>
                            <div style={{ fontSize: '18px', color: 'white', margin: '5px 0' }}>{boost.val}</div>

                            {isActive ? (
                                <div style={{ fontSize: '11px', color: '#00ff00', marginTop: '10px' }}>
                                    Expires: <br />{expiryDate}
                                </div>
                            ) : (
                                <button
                                    onClick={() => handleActivate(boost.id)}
                                    disabled={count === 0 || activating !== null || isLoading}
                                    className="modern-button"
                                    style={{
                                        marginTop: '10px',
                                        width: '100%',
                                        background: count > 0 ? 'linear-gradient(145deg, #ff4b2b, #ff416c)' : '#444',
                                        cursor: count > 0 ? 'pointer' : 'not-allowed',
                                        fontSize: '12px'
                                    }}
                                >
                                    {activating === boost.id ? 'Activating...' : isLoading ? 'Scan...' : `Activate (${count})`}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DailyBoosts;